import * as Dialog from "@radix-ui/react-dialog";

import { useCallback, useEffect, useRef, useState } from "react";

import { DEFAULT_TRIGGER_ICON_PROPS, triggerButtonClasses } from "./ContextMenu.styles";

import { ContextMenuContentProps } from "./ContextMenu.types";
import { Icon } from "../Icon";
import { MenuItems } from "./MenuItems";
import clsx from "clsx";

const ANIMATION_DURATION = 300;

export const ContextMenuContentMobile = ({
  menuItems,
  trigger,
  triggerClassName,
  triggerIconProps = DEFAULT_TRIGGER_ICON_PROPS,
  triggerTestId,
  disabled = false,
  onOpenChange,
  noBorder = false,
}: ContextMenuContentProps) => {
  const [open, setOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = useCallback(() => {
    if (!closeTimeoutRef.current) return;
    clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = null;
  }, []);

  useEffect(() => {
    return () => clearCloseTimeout();
  }, [clearCloseTimeout]);

  const handleOpen = useCallback(() => {
    const result = onOpenChange?.(true) as boolean | void | undefined;
    if (result === false) return;
    clearCloseTimeout();
    setIsVisible(true);
    setIsExiting(false);
    setOpen(true);
  }, [onOpenChange, clearCloseTimeout]);

  const handleClose = useCallback(() => {
    const result = onOpenChange?.(false) as boolean | void | undefined;
    if (result === false) return;
    setIsExiting(true);
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      setIsExiting(false);
      setIsVisible(false);
      closeTimeoutRef.current = null;
    }, ANIMATION_DURATION);
  }, [onOpenChange, clearCloseTimeout]);

  return (
    <div data-testid="context-menu-mobile" onClick={(e) => e.stopPropagation()}>
      <Dialog.Root open={open} onOpenChange={(nextOpen) => (nextOpen ? handleOpen() : handleClose())}>
        {trigger ? (
          <Dialog.Trigger asChild disabled={disabled} className={triggerClassName} data-testid={triggerTestId}>
            {trigger}
          </Dialog.Trigger>
        ) : (
          <Dialog.Trigger
            className={triggerButtonClasses(triggerClassName, open)}
            data-testid={triggerTestId}
            disabled={disabled}
          >
            <Icon {...triggerIconProps} />
          </Dialog.Trigger>
        )}

        {isVisible && (
          <Dialog.Portal>
            <Dialog.Overlay
              onClick={() => {
                handleClose();
              }}
              className="fixed inset-0 bg-black/40 z-50"
            />
            <Dialog.Content
              className={clsx(
                "fixed left-0 right-0 bottom-0 z-50 w-full max-h-[80vh] rounded-t-2xl bg-white p-4 flex flex-col border-none outline-none",
                isExiting ? "animate-drawer-slide-down" : "animate-drawer-slide-up"
              )}
            >
              <div className="flex flex-col">
                <MenuItems items={menuItems} noBorder={noBorder} onClose={handleClose} />
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </Dialog.Root>
    </div>
  );
};
